import { forEach, values } from 'lodash';
import BaseClass from '@core/common/BaseClass';

/**
 * The Default API Version to Use
 */
const DEFAULT_API_VERSION = '2';


/**
 * UMS Connection Proxy to the AMS Client.
 */
export default class UMSConnection extends BaseClass {
  umsClientInstance = null;
  connectionState = null;
  lastPingResponse = null;

  /**
   * Create a new Connection Wrapper for AMS functionality.
   *
   * @constructor
   * @param {Object} config - configuration object
   * @param {String} config.accountId - the account id
   * @param {String} config.token - the logged in agent token
   * @param {String}  config.domain - the UMS domain
   * @param {String}  config.apiVersion
   * @param {String}  config.memberType - expected member type - AGENT
   */
  constructor(config) {
    const { AMSStatics } = window.lpTag;
    super({ name: 'UMSConnection', type: 'Connection' });
    this.accountId = config.accountId;
    this.token = config.token;
    this.domain = config.domain;
    this.apiVersion = config.apiVersion || DEFAULT_API_VERSION;
    this.memberType = config.memberType;
    this.CONNECTIONS_STATES = {
      CONNECTING: 'connecting',
      CONNECTED: 'connected',
      NON_RESPONSIVE: 'non-responsive',
      DISCONNECTED: 'disconnected',
    };
    this.EVENTS = {
      CONNECTION_STATE_CHANGE: 'CONNECTION_STATE_CHANGE',
      // use AMSStatics directly from window to ensure AMSStatics exists during initialization
      onExConversationNotification: AMSStatics.events.EX_CONVERSATION_NOTIFICATION,
    };
  }

  get umsClient() {
    return this.umsClientInstance;
  }

  set umsClient(value) {
    this.umsClientInstance = value;
  }

  /**
   * Connect to the UMS Server.
   *
   * @emits CONNECTION_STATE_CHANGE
   */
  openConnection() {
    const clientSettings = this.getClientSettings();
    this.logger.info(`Opening a connections to the UMS Server ( ${clientSettings.domain} )`, 'openConnection');
    return new Promise((resolve) => {
      // starting the connecting flow. setting connecting state
      this.updateConnectionState(this.CONNECTIONS_STATES.CONNECTING);
      this.umsClient = new window.lpTag.AMSClient(); // use AMSClient directly from the window
      this.umsClient.init(clientSettings);
      this.on(
        this.EVENTS.CONNECTION_STATE_CHANGE,
        data => this.onConnectionUpdate(data, resolve),
        'UMSConnectionStateChange',
      );
    });
  }

  /**
   * Disconnect from the UMS Server.
   *
   * @emits CONNECTION_STATE_CHANGE
   */
  closeConnection() {
    if (this.umsClient) {
      if (this.umsClient.dispose) {
        this.umsClient.dispose();
      }
      this.umsClient = null;
    }
    this.updateConnectionState(this.CONNECTIONS_STATES.DISCONNECTED);
  }

  /**
   * Disconnect from a current connection if has any,
   * and Reconnect to the UMS Servers.
   *
   * @emits CONNECTION_STATE_CHANGE
   */
  resetConnection() {
    this.closeConnection();
    this.openConnection();
  }

  /**
   * get the setting for the AMSClient instance.
   */
  getClientSettings() {
    // the tunnel events are redirected to the consumer of the connection.
    const tEvents = {};
    const tunnelEvents = () => {
      forEach(values(this.EVENTS), (item) => {
        tEvents[item] = {
          func: (eventData) => {
            this.emit(item, eventData);
          },
          context: this,
        };
      });
      return tEvents;
    };

    function onSocketConnected() {
      this.updateConnectionState(this.CONNECTIONS_STATES.CONNECTED);
    }

    function onSocketClosed() {
      this.updateConnectionState(this.CONNECTIONS_STATES.DISCONNECTED);
    }

    function onPingSuccess() {
      this.lastPingResponse = Date.now();
    }

    function onServiceNonResponsive() {
      this.updateConnectionState(this.CONNECTIONS_STATES.NON_RESPONSIVE);
    }

    // creating the UMS client settings
    return Object.assign(
      {
        apiVersion: this.apiVersion,
        token: this.token,
        memberType: this.memberType,
        domain: this.domain,
        accountId: this.accountId,
      },
      {
        ...tunnelEvents(),
        onSocketConnected: { func: onSocketConnected, context: this },
        onSocketClosed: { func: onSocketClosed, context: this },
        onServiceNonResponsive: { func: onServiceNonResponsive, context: this },
        onPingSuccess: { func: onPingSuccess, context: this },
      },
    );
  }

  /**
   * Update the Connection state of the Component
   *
   * @param {*} newState - the new state of the connection
   * @emits CONNECTION_STATE_CHANGE
   */
  updateConnectionState(newState) {
    if (this.connectionState !== newState) {
      const oldState = this.connectionState;
      this.connectionState = newState;
      this.logger.debug(`UMS Connection state changed from: (${oldState}) to: (${newState})`, 'updateConnectionState');
      this.emit(this.EVENTS.CONNECTION_STATE_CHANGE, {
        newValue: newState,
        oldValue: oldState,
      });
    }
  }

  onConnectionUpdate(data, resolve) {
    if (data.newValue === 'connected') {
      this.logger.info('Connected to UMS Server.');
      this.updateConnectionState(this.CONNECTIONS_STATES.CONNECTED);
      resolve();
      this.off(this.EVENTS.CONNECTION_STATE_CHANGE, () => this.onConnectionUpdate(resolve),
        'UMSConnectionStateChange');
    }
  }
}
